// src/Pages/Auth/variants/IndustryNarrative.jsx
//
// Renders any entry from industries.js. All copy lives there; this file is layout only.
// Same contract as the other variants: ({ compact, ctx }), owns its outer Box.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Link, Divider } from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  ArrowRightAlt as ArrowFlowIcon,
  SubdirectoryArrowRight as ArrowDownIcon,
  PersonAdd as AccountIcon,
  Storefront as BusinessIcon,
  Link as ConnectIcon,
} from '@mui/icons-material';
import { resolveIndustry, KIND } from './industries';

const FlowCard = ({ compact, c }) => (
  <Box sx={{ p: compact ? 1.75 : 2.25, bgcolor: 'white', borderRadius: 2.5, border: '1px solid #E8EDF2', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
    <Typography
      variant="overline"
      sx={{
        color: '#90A4AE', letterSpacing: compact ? 1.8 : 2.5, fontSize: compact ? 10 : 11, fontWeight: 700,
        display: 'block', mb: compact ? 1.5 : 1.75, textAlign: compact ? 'center' : 'left', lineHeight: 1.4,
      }}
    >
      {c.flowTitle}
    </Typography>

    {c.flow.map(({ Icon, kind, signal, signalAt, outcome, outcomeAt, mobileOutcome }) => {
      const k = KIND[kind] || KIND.social;
      return (
        <Box
          key={signal}
          sx={{
            pb: 1.4, mb: 1.4, borderBottom: '1px dashed #E8EDF2',
            '&:last-of-type': { pb: 0, mb: 0, borderBottom: 'none' },
            ...(compact ? {} : { display: 'flex', alignItems: 'center', gap: 1.5 }),
          }}
        >
          {!compact && (
            <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: k.bg, border: `1px solid ${k.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon sx={{ color: k.color, fontSize: 18 }} />
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 0 }}>
              {compact && <Icon sx={{ color: k.color, fontSize: 15, flexShrink: 0 }} />}
              <Typography variant="caption" sx={{ color: '#78909C', fontSize: compact ? 12 : 12.5, lineHeight: 1.4 }}>
                {signal}
                {signalAt && (
                  <Box component="span" sx={{ color: '#B0BEC5' }}>
                    {compact ? `, ${signalAt}` : ` · ${signalAt}`}
                  </Box>
                )}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#0D2B6B', fontWeight: 600, fontSize: compact ? 12.5 : 13.5, lineHeight: 1.45, mt: 0.4,
                pl: compact ? '23px' : 0, display: 'flex', alignItems: 'flex-start', gap: 0.6,
              }}
            >
              {compact
                ? <ArrowDownIcon sx={{ color: k.color, fontSize: 14, mt: '2px', flexShrink: 0 }} />
                : <ArrowFlowIcon sx={{ color: k.color, fontSize: 17, mt: '-1px', flexShrink: 0 }} />}
              {compact ? mobileOutcome : outcome}
            </Typography>
          </Box>

          {!compact && outcomeAt && (
            <Box sx={{ flexShrink: 0, px: 1.25, py: 0.4, borderRadius: 5, bgcolor: k.bg }}>
              <Typography variant="caption" sx={{ color: k.color, fontWeight: 700, fontSize: 11 }}>{outcomeAt}</Typography>
            </Box>
          )}
        </Box>
      );
    })}

    {c.flowFooter && (
      <Box sx={{ mt: 1.4, pt: 1.2, borderTop: '1px dashed #E8EDF2' }}>
        <Typography variant="caption" sx={{ color: '#455A7B', fontSize: compact ? 11.5 : 12, fontWeight: 500, lineHeight: 1.5, display: 'block', textAlign: compact ? 'center' : 'left' }}>
          {c.flowFooter}
        </Typography>
      </Box>
    )}
  </Box>
);

// Up to 3 stats; note says where they come from (sample, region, date).
const StatStrip = ({ compact, stats, note }) => (
  <Box sx={{ mb: compact ? 2 : 3 }}>
    <Box sx={{ display: 'flex', bgcolor: '#F0F7FF', borderRadius: 2, border: '1px solid #BBDEFB', overflow: 'hidden' }}>
      {stats.slice(0, 3).map(({ value, label }, i, arr) => (
        <React.Fragment key={label}>
          <Box sx={{ flex: 1, textAlign: 'center', py: 1.5, px: 1 }}>
            <Typography sx={{ fontWeight: 800, color: '#1565C0', lineHeight: 1, mb: 0.5, fontSize: compact ? '1rem' : '1.35rem' }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#546E7A', fontWeight: 500, fontSize: compact ? 10 : 11, lineHeight: 1.3, display: 'block' }}>
              {label}
            </Typography>
          </Box>
          {i < arr.length - 1 && <Divider orientation="vertical" flexItem sx={{ borderColor: '#BBDEFB' }} />}
        </React.Fragment>
      ))}
    </Box>
    {note && (
      <Typography variant="caption" sx={{ color: '#90A4AE', fontSize: 10.5, display: 'block', mt: 0.75, textAlign: compact ? 'center' : 'left' }}>
        {note}
      </Typography>
    )}
  </Box>
);

const SetupCard = ({ compact, c }) => {
  const k = KIND[c.accent] || KIND.social;
  const steps = [
    { Icon: AccountIcon, label: 'Create your account', detail: 'Email or Google, about a minute.' },
    { Icon: BusinessIcon, label: 'Add your business', detail: c.addBusiness },
    { Icon: ConnectIcon, label: c.connect.label, detail: c.connect.detail },
  ];
  return (
    <Box sx={{ p: compact ? 2 : 2.5, bgcolor: 'white', borderRadius: 2.5, border: '1px solid #E8EDF2', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Typography variant="overline" sx={{ color: '#90A4AE', letterSpacing: 2.5, fontSize: 10, fontWeight: 700, display: 'block', mb: 2 }}>
        WHAT HAPPENS AFTER YOU SIGN UP
      </Typography>

      {steps.map(({ Icon, label, detail }, i) => (
        <Box
          key={label}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, pb: 2, borderBottom: '1px dashed #E8EDF2',
            '&:last-child': { mb: 0, pb: 0, borderBottom: 'none' },
          }}
        >
          <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: k.bg, border: `1px solid ${k.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ color: k.color, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={700} color="#0D2B6B" sx={{ mb: 0.25 }}>{i + 1}. {label}</Typography>
            <Typography variant="caption" color="#546E7A" sx={{ lineHeight: 1.5, display: 'block' }}>{detail}</Typography>
          </Box>
        </Box>
      ))}

      <Box sx={{ pt: 1.5, mt: 0.5, borderTop: '1px dashed #E8EDF2' }}>
        <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 700, fontSize: 11, display: 'block', mb: 0.5 }}>
          Free tier available · $19/mo launch pricing
        </Typography>
        <Typography variant="caption" sx={{ color: '#90A4AE', fontWeight: 500, fontSize: 11 }}>
          No credit card to start · Regular $49/mo after launch
        </Typography>
      </Box>
    </Box>
  );
};

const SecondaryLinks = ({ center = false }) => {
  const navigate = useNavigate();
  const linkSx = { color: '#1976D2', fontWeight: 600, fontSize: 13, cursor: 'pointer', border: 'none', background: 'none', '&:hover': { color: '#1565C0' } };
  return (
    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid rgba(13,43,107,0.1)', ...(center ? { textAlign: 'center' } : {}) }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
        Want premium free, permanently?{' '}
        <Link component="button" onClick={() => navigate('/founders-circle')} underline="none" sx={linkSx}>Join the Founders Circle →</Link>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        Want the longer version?{' '}
        <Link component="button" onClick={() => navigate('/landing/business')} underline="none" sx={linkSx}>
          See how it works<ArrowIcon sx={{ fontSize: 15, verticalAlign: 'text-bottom', ml: 0.25 }} />
        </Link>
      </Typography>
    </Box>
  );
};

export default function IndustryNarrative({ compact = false, ctx = {} }) {
  const c = resolveIndustry(ctx);
  if (!c) return null; // the rule already checked; this only guards direct misuse

  const k = KIND[c.accent] || KIND.social;
  const chip = (
    <Chip
      label={c.chip}
      size="small"
      sx={{
        mb: compact ? 1.5 : 3, bgcolor: k.bg, color: k.color, fontWeight: 700, fontSize: 11,
        border: `1px solid ${k.border}`, height: 'auto', py: 0.75,
        '& .MuiChip-label': { whiteSpace: 'normal', ...(compact ? { textAlign: 'center' } : {}) },
      }}
    />
  );
  const secondary = c.secondary && (
    <Typography variant="caption" sx={{ color: '#546E7A', fontSize: 12, lineHeight: 1.6, display: 'block', mt: 2 }}>
      {c.secondary}
    </Typography>
  );

  if (compact) {
    return (
      <Box sx={{ width: '100%', mb: 3, px: { xs: 1, sm: 0 } }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          {chip}
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0D2B6B', lineHeight: 1.25, mb: 1.5, fontSize: { xs: '1.35rem', sm: '1.6rem' } }}>
            {c.headline}<br />
            <Box component="span" sx={{ color: '#1976D2' }}>{c.headlineAccent}</Box>
          </Typography>
          <Typography variant="body2" sx={{ color: '#455A7B', lineHeight: 1.65, maxWidth: 420, mx: 'auto', fontSize: { xs: 13, sm: 14 } }}>
            {c.subhead}
          </Typography>
        </Box>
        {c.stats?.length > 0 && <StatStrip compact={true} stats={c.stats} note={c.statsNote} />}
        <Box sx={{ mb: 2 }}><FlowCard compact={true} c={c} /></Box>
        <Box sx={{ mb: 2 }}><SetupCard compact={true} c={c} /></Box>
        {secondary && <Box sx={{ textAlign: 'center' }}>{secondary}</Box>}
        <SecondaryLinks center={true} />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 5, pt: 2 }}>
      {chip}
      <Typography variant="h2" component="h1" sx={{ fontWeight: 800, color: '#0D2B6B', lineHeight: 1.15, mb: 2.5, fontSize: { lg: '2.6rem' } }}>
        {c.headline}<br />
        <Box component="span" sx={{ color: '#1976D2' }}>{c.headlineAccent}</Box>
      </Typography>
      <Typography variant="body1" sx={{ color: '#455A7B', lineHeight: 1.75, fontSize: '1rem', mb: 3.5 }}>
        {c.subhead}
      </Typography>
      {c.stats?.length > 0 && <StatStrip compact={false} stats={c.stats} note={c.statsNote} />}
      <Box sx={{ mb: 3 }}><FlowCard compact={false} c={c} /></Box>
      <Box sx={{ mb: 3 }}><SetupCard compact={false} c={c} /></Box>
      {secondary}
      <SecondaryLinks center={false} />
    </Box>
  );
}